import { Box, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import { ReactNode, useEffect, useMemo, useState } from "react";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";

import ClientAppearanceForm from "../components/ClientAppearanceForm";
import ClientMiscForm from "../components/ClientMiscForm";
import ClientPwaNotificationsForm from "../components/ClientPwaNotificationsForm";
import ClientSecurityForm from "../components/ClientSecurityForm";
import { isDesktopShellEnvironment } from "../utils/desktopShell";

type PreferencesTab = {
  value: string;
  label: string;
  content: ReactNode;
};

const preferencesTabs: PreferencesTab[] = [
  { value: "security", label: "Security", content: <ClientSecurityForm /> },
  { value: "pwa", label: "PWA", content: <ClientPwaNotificationsForm /> },
  { value: "appearance", label: "Appearance", content: <ClientAppearanceForm /> },
  { value: "misc", label: "Misc", content: <ClientMiscForm /> }
];

const tabProps = (value: string) => ({
  id: `client-preferences-tab-${value}`,
  "aria-controls": `client-preferences-tabpanel-${value}`
});

const ClientPreferencesPage = () => {
  const isDesktopShell = useMemo(() => isDesktopShellEnvironment(), []);
  const visibleTabs = useMemo(
    () => preferencesTabs.filter((tab) => !isDesktopShell || (tab.value !== "security" && tab.value !== "pwa")),
    [isDesktopShell]
  );
  const [selectedTab, setSelectedTab] = useState(visibleTabs[0].value);

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.value === selectedTab)) {
      setSelectedTab(visibleTabs[0].value);
    }
  }, [selectedTab, visibleTabs]);

  const activeTab = useMemo(
    () => visibleTabs.find((tab) => tab.value === selectedTab)?.value ?? visibleTabs[0].value,
    [selectedTab, visibleTabs]
  );

  return (
    <Box
      component="section"
      sx={{
        maxWidth: 720,
        width: "100%",
        mx: "auto",
        py: 4,
        px: 2
      }}
    >
      <Stack spacing={3}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ManageAccountsIcon color="primary" />
            <Typography variant="h4" sx={{ m: 0 }}>
              Client preferences
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Settings on this page are saved locally in your browser or installed PWA.
          </Typography>
        </Box>
        <Paper sx={{ p: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => {
              setSelectedTab(value);
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Client preference categories"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            {visibleTabs.map((tab) => (
              <Tab key={tab.value} label={tab.label} value={tab.value} {...tabProps(tab.value)} />
            ))}
          </Tabs>
          {visibleTabs.map((tab) => (
            <Box
              key={tab.value}
              role="tabpanel"
              hidden={activeTab !== tab.value}
              id={`client-preferences-tabpanel-${tab.value}`}
              aria-labelledby={`client-preferences-tab-${tab.value}`}
              sx={{ pt: 3 }}
            >
              {activeTab === tab.value && tab.content}
            </Box>
          ))}
        </Paper>
      </Stack>
    </Box>
  );
};

export default ClientPreferencesPage;
